import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import type { EventType } from '@/types';

export type LiveActionZone = 'flow' | 'discipline' | 'technical';

export interface LiveActionDefinition {
  key: EventType;
  label: string;
  zone: LiveActionZone;
}

interface Props {
  disabled?: boolean;
  onActionTap: (action: LiveActionDefinition) => void;
  /** Single selection highlight (legacy) */
  selectedKey?: string | null;
  /** Multi-select highlights */
  selectedKeys?: Set<string>;
  multiSelect?: boolean;
  /** iPad / fullscreen — larger touch targets */
  largeTouch?: boolean;
  /** Limit visible zones (default: all). */
  zones?: LiveActionZone[];
}

const ZONE_1_ACTIONS: LiveActionDefinition[] = [
  { key: 'play_on', label: 'Play On', zone: 'flow' },
  { key: 'goal_kick', label: '龍門球', zone: 'flow' },
  { key: 'free_throw', label: '自由球', zone: 'flow' },
  { key: 'throw_in', label: '界外球', zone: 'flow' },
  { key: 'seven_meter', label: '七米球', zone: 'flow' },
  { key: 'opposite_free_throw', label: '對隊自由球', zone: 'flow' },
];

const ZONE_2_ACTIONS: LiveActionDefinition[] = [
  { key: 'verbal_warning', label: '口頭警告', zone: 'discipline' },
  { key: 'yellow_card', label: '黃牌', zone: 'discipline' },
  { key: 'two_minutes', label: '兩分鐘', zone: 'discipline' },
  { key: 'red_card', label: '紅牌', zone: 'discipline' },
  { key: 'blue_card', label: '藍牌', zone: 'discipline' },
  { key: 'no_penalty', label: '無罰則', zone: 'discipline' },
];

export const ZONE_3_FOUL_ACTIONS: LiveActionDefinition[] = [
  { key: 'steps', label: '走步', zone: 'technical' },
  { key: 'double', label: 'Double', zone: 'technical' },
  { key: 'charging', label: '撞人', zone: 'technical' },
  { key: 'kicking', label: 'Kicking', zone: 'technical' },
  { key: 'goal_area_violation', label: '越區', zone: 'technical' },
  { key: 'blocking', label: '閂閘', zone: 'technical' },
  { key: 'offensive_foul', label: '進攻犯規', zone: 'technical' },
  { key: 'pivot', label: '中鋒', zone: 'technical' },
  { key: 'tripping', label: '攝腳', zone: 'technical' },
  { key: 'pushing_in_air', label: '空中推人', zone: 'technical' },
  { key: 'team_timeout', label: '隊暫停', zone: 'technical' },
  { key: 'bench_foul', label: '替補犯規', zone: 'technical' },
  { key: 'long_holding', label: 'Long Holding', zone: 'technical' },
  { key: 'pull_down', label: 'Pull Down', zone: 'technical' },
  { key: 'face_hit', label: '打面', zone: 'technical' },
  { key: 'goalie_head_hit', label: '龍門爆頭', zone: 'technical' },
  { key: 'jersey_pull', label: '拉衫', zone: 'technical' },
  { key: 'diving', label: '插水', zone: 'technical' },
  { key: 'empty_goal', label: '空門', zone: 'technical' },
  { key: 'last_30_seconds', label: '最後30秒', zone: 'technical' },
];

export const LIVE_ACTION_DEFINITIONS: LiveActionDefinition[] = [
  ...ZONE_1_ACTIONS,
  ...ZONE_2_ACTIONS,
  ...ZONE_3_FOUL_ACTIONS,
];

export const LIVE_CATEGORY_LABELS: Record<LiveActionZone, string> = {
  flow: '球判決',
  discipline: '判罰',
  technical: '犯規事件',
};

const ZONE_DISPLAY_ORDER: LiveActionZone[] = ['flow', 'discipline', 'technical'];

/** Short zone labels joined for multi-mark cards, e.g. "球判決 + 判罰". */
export function formatCombinedZoneLabels(eventTypes: string[]): string {
  const zones = new Set<LiveActionZone>();
  for (const eventType of eventTypes) {
    const zone = getLiveEventZone(eventType);
    if (zone) zones.add(zone);
  }
  const labels = ZONE_DISPLAY_ORDER.filter((zone) => zones.has(zone)).map((zone) => LIVE_CATEGORY_LABELS[zone]);
  return labels.length > 0 ? labels.join(' + ') : '其他';
}

const EVENT_ZONE_LOOKUP = new Map<string, LiveActionZone>(
  LIVE_ACTION_DEFINITIONS.map((action) => [action.key, action.zone])
);

export function getLiveEventZone(eventType: string): LiveActionZone | null {
  return EVENT_ZONE_LOOKUP.get(eventType) ?? null;
}

export function getLiveEventCategoryLabel(eventType: string): string {
  const zone = getLiveEventZone(eventType);
  if (!zone) return '其他';
  return LIVE_CATEGORY_LABELS[zone];
}

export function findLiveActionDefinition(eventType: string): LiveActionDefinition | undefined {
  return LIVE_ACTION_DEFINITIONS.find((action) => action.key === eventType);
}

const actionButtonSx = (large: boolean) =>
  ({
    minHeight: large ? 64 : 56,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: large ? '1rem' : '0.9rem',
    borderRadius: 1.25,
    border: '1px solid',
    bgcolor: 'background.paper',
    borderColor: 'divider',
    color: 'text.primary',
    '&:hover': { bgcolor: 'action.hover' },
  }) as const;

function ZoneCard({
  title,
  subtitle,
  bg,
  accent,
  children,
}: {
  title: string;
  subtitle: string;
  bg: string;
  accent: string;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: bg,
        border: '1px solid',
        borderColor: accent,
      }}
    >
      <Typography variant="subtitle2" fontWeight={800} sx={{ color: accent }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {subtitle}
      </Typography>
      {children}
    </Box>
  );
}

export default function LiveActionZones({
  disabled,
  onActionTap,
  selectedKey,
  selectedKeys,
  multiSelect = false,
  largeTouch = false,
  zones,
}: Props) {
  const theme = useTheme();
  const narrow = useMediaQuery('(max-width:360px)');
  const zonesToShow = zones ?? ZONE_DISPLAY_ORDER;

  const zone1Bg = theme.palette.mode === 'dark' ? 'rgba(21,101,192,0.14)' : '#E3F2FD';
  const zone2Bg = theme.palette.mode === 'dark' ? 'rgba(198,40,40,0.16)' : '#FFEBEE';
  const zone3Bg = theme.palette.mode === 'dark' ? 'rgba(245,124,0,0.14)' : '#FFF8E1';

  const gridSx = (cols: number) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gap: 1,
  });

  const renderActionButton = (action: LiveActionDefinition) => {
    const selected = multiSelect
      ? Boolean(selectedKeys?.has(action.key))
      : selectedKey === action.key;
    return (
      <Button
        key={action.key}
        disabled={disabled}
        onClick={() => onActionTap(action)}
        variant={selected ? 'contained' : 'outlined'}
        color={selected ? 'secondary' : 'inherit'}
        sx={{
          ...actionButtonSx(largeTouch),
          ...(selected
            ? {
                borderColor: 'secondary.main',
                borderWidth: 2,
                fontWeight: 800,
              }
            : {}),
        }}
      >
        {action.label}
      </Button>
    );
  };

  return (
    <Stack spacing={1.25}>
      {zonesToShow.includes('flow') && (
        <ZoneCard title="Zone 1 · 球判決" subtitle="重開球與球判決" bg={zone1Bg} accent="#1565c0">
          <Box sx={gridSx(3)}>{ZONE_1_ACTIONS.map(renderActionButton)}</Box>
        </ZoneCard>
      )}

      {zonesToShow.includes('discipline') && (
        <ZoneCard title="Zone 2 · 判罰" subtitle="牌罰與警告" bg={zone2Bg} accent="#b71c1c">
          <Box sx={gridSx(3)}>{ZONE_2_ACTIONS.map(renderActionButton)}</Box>
        </ZoneCard>
      )}

      {zonesToShow.includes('technical') && (
        <ZoneCard title="Zone 3 · 犯規事件" subtitle="身體接觸與技術犯規" bg={zone3Bg} accent="#ef6c00">
          <Box sx={gridSx(narrow ? 2 : 4)}>{ZONE_3_FOUL_ACTIONS.map(renderActionButton)}</Box>
        </ZoneCard>
      )}
    </Stack>
  );
}
